import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import { ClinicalTrainingEventsState, ClinicalTrainingEventsActions, ClinicalTrainingEventsFilters } from './types';
import { ClinicalTrainingEventsMutations } from './types';
import ClinicalTrainingEvent, { ClinicalTrainingEventStatus } from './clinical-training-event';
import { ClinicalTrainingEventJSON, ClinicalTrainingEventModal } from './clinical-training-event';
import Image from '@/store/models/image';
import { ListStatus, EditableStatus } from '@/store/types';
import { RootActions } from '@/store/types';
import { ErrorMessages } from '@/store/types';
import { enhanceErrorWithMessage } from '@/utils/errors';
import api from '@/api';
import router from '@/router';
import i18next from '@/i18n';
import { NotificationsActions } from '@/store/modules/notifications/types';

export const actions: ActionTree<ClinicalTrainingEventsState, RootState> = {
  [ClinicalTrainingEventsActions.LOAD_ALL]({ commit, dispatch }, filters = {}): Promise<void> {
    commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return api.clinicalTrainingEvents
      .list(filters)
      .then((response) => response.data)
      .then((response) => {
        const events = response.data.map((event: ClinicalTrainingEventJSON) => ClinicalTrainingEvent.fromJSON(event));
        commit(ClinicalTrainingEventsMutations.SET_ALL, events);
        commit(ClinicalTrainingEventsMutations.SET_LIST_PAGINATION, {
          currentPage: response.current_page,
          lastPage: response.last_page,
          total: response.total,
        });
        commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.LOAD_ACTIVE]({ commit, dispatch }): Promise<void> {
    commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return api.clinicalTrainingEvents
      .active()
      .then((response) => response.data)
      .then((events) => {
        const clinicalTrainingEvents = events.map((eventJson: ClinicalTrainingEventJSON) => {
          // Handle both single image and array of images (like ads do)
          const image = Array.isArray(eventJson.image) 
            ? eventJson.image[0] 
            : eventJson.image;

          const modalEvent: ClinicalTrainingEventModal = {
            id: eventJson.id,
            name: eventJson.name,
            eventUrl: eventJson.event_url,
            image: image?.thumbnail || '',
            startDate: eventJson.start_date,
            endDate: eventJson.end_date,
            notes: eventJson.notes || '',
            participantCount: eventJson.participant_count,
            isFull: eventJson.is_full,
            isUserSubscribed: eventJson.is_user_subscribed,
          };
          
          return modalEvent;
        });
        commit(ClinicalTrainingEventsMutations.SET_ALL, clinicalTrainingEvents);
        commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.LOAD_EDITABLE]({ commit, dispatch }, { id } = {}): Promise<void> {
    commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Loading);

    if (!id) {
      const event = new ClinicalTrainingEvent();
      event.status = ClinicalTrainingEventStatus.Active;
      commit(ClinicalTrainingEventsMutations.SET_EDITABLE, event);
      commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      return Promise.resolve();
    }

    return api.clinicalTrainingEvents
      .get(id)
      .then((response) => response.data)
      .then((response) => {
        const event = ClinicalTrainingEvent.fromJSON(response);
        commit(ClinicalTrainingEventsMutations.SET_EDITABLE, event);
        commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.UPDATE_EDITABLE]({ commit }, changes): Promise<void> {
    commit(ClinicalTrainingEventsMutations.UPDATE_EDITABLE, changes);
    return Promise.resolve();
  },

  [ClinicalTrainingEventsActions.DELETE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-clinical-training-event';
    if (isEdit) {
      commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => api.clinicalTrainingEvents.remove(id))
      .then(() => {
        if (isEdit) {
          commit(ClinicalTrainingEventsMutations.UPDATE_INITIAL_EDITABLE, {
            status: ClinicalTrainingEventStatus.Inactive,
          });
          commit(ClinicalTrainingEventsMutations.UPDATE_EDITABLE, {
            status: ClinicalTrainingEventStatus.Inactive,
          });
          commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(ClinicalTrainingEventsActions.LOAD_ALL, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The clinical training event was deleted successfully.'),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Failed);
        }
        
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.RESTORE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-clinical-training-event';
    if (isEdit) {
      commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => api.clinicalTrainingEvents.restore(id))
      .then(() => {
        if (isEdit) {
          commit(ClinicalTrainingEventsMutations.UPDATE_INITIAL_EDITABLE, {
            status: ClinicalTrainingEventStatus.Active,
          });
          commit(ClinicalTrainingEventsMutations.UPDATE_EDITABLE, {
            status: ClinicalTrainingEventStatus.Active,
          });
          commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(ClinicalTrainingEventsActions.LOAD_ALL, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() =>
        dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The clinical training event was restored successfully.'),
          },
          { root: true },
        ),
      )
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Failed);
        }

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.APPLY_FILTERS]({ dispatch }, filters: ClinicalTrainingEventsFilters): Promise<void> {
    return dispatch(ClinicalTrainingEventsActions.LOAD_ALL, filters);
  },

  [ClinicalTrainingEventsActions.LOAD_PRODUCTS]({ commit, dispatch }): Promise<void> {
    return api.products
      .listTree()
      .then((response) => {
        commit(ClinicalTrainingEventsMutations.SET_PRODUCTS, response.data);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.SUBSCRIBE]({ commit, dispatch, state }, { id }: { id: number }): Promise<void> {
    return api.clinicalTrainingEvents
      .subscribe(id)
      .then(() => {
        // Update the local event state to show as subscribed
        const eventIndex = state.list.findIndex(event => event.id === id);
        if (eventIndex !== -1) {
          const updatedEvent: ClinicalTrainingEventModal = { ...state.list[eventIndex] };
          updatedEvent.isUserSubscribed = true;
          commit(ClinicalTrainingEventsMutations.UPDATE_EVENT, { index: eventIndex, event: updatedEvent });
        }
        
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('Successfully subscribed to the clinical training event.'),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.UNSUBSCRIBE]({ commit, dispatch, state }, { id }: { id: number }): Promise<void> {
    return api.clinicalTrainingEvents
      .unsubscribe(id)
      .then(() => {
        // Update the local event state to show as unsubscribed
        const eventIndex = state.list.findIndex(event => event.id === id);
        if (eventIndex !== -1) {
          const updatedEvent: ClinicalTrainingEventModal = { ...state.list[eventIndex] };
          updatedEvent.isUserSubscribed = false;
          commit(ClinicalTrainingEventsMutations.UPDATE_EVENT, { index: eventIndex, event: updatedEvent });
        }
        
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('Successfully unsubscribed from the clinical training event.'),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [ClinicalTrainingEventsActions.SAVE]({ state, commit, dispatch }): Promise<void> {
    let isUpdate: boolean;
    let url: string;
    let successMsg: string;

    return new Promise<void>((resolve, reject) => {
      if (!state.editable) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => {
        commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);

        isUpdate = Boolean(state.editable!.id);
        url = 'clinical-training-event';
        successMsg = i18next.t('A new clinical training event was added successfully.');

        if (isUpdate) {
          url += `/${state.editable!.id}`;
          successMsg = i18next.t('The clinical training event was successfully updated.');
        }
      })
      .then(() => api.clinicalTrainingEvents.store(state.editable!.toJSON()))
      .then((response) => response.data)
      .then((response): Promise<ClinicalTrainingEvent> => {
        const event = ClinicalTrainingEvent.fromJSON(response);

        commit(ClinicalTrainingEventsMutations.SET_EDITABLE, event);
        commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);

        if (!isUpdate) {
          return new Promise((resolve, reject) => {
            if (!event.id) {
              return reject(new Error('Invalid data.'));
            }

            router.replace(
              {
                name: 'edit-clinical-training-event',
                params: {
                  id: String(event.id),
                },
              },
              () => resolve(event),
              (error: any) => reject(error),
            );
          });
        }

        return Promise.resolve(event);
      })
      .then(() => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: successMsg,
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(ClinicalTrainingEventsMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [ClinicalTrainingEventsActions.BULK_DELETE]({ commit, dispatch }, { ids = [] }: { ids: number[] } = { ids: [] }): Promise<void> {
    commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return new Promise<void>((resolve, reject) => {
      if (!ids || !ids.length) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => Promise.all(ids.map((id) => api.clinicalTrainingEvents.remove(id))))
      .then(() => {
        dispatch(ClinicalTrainingEventsActions.LOAD_ALL, {
          ...router.currentRoute.params,
          ...router.currentRoute.query,
        });

        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('%(count)% clinical training event was deleted successfully', { count: ids.length }),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Failed);

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
  [ClinicalTrainingEventsActions.BULK_RESTORE]({ commit, dispatch }, { ids = [] }: { ids: number[] } = { ids: [] }): Promise<void> {
    commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Loading);

    return new Promise<void>((resolve, reject) => {
      if (!ids || !ids.length) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => Promise.all(ids.map(async (id) => api.clinicalTrainingEvents.restore(id))))
      .then(() => {
        dispatch(ClinicalTrainingEventsActions.LOAD_ALL, {
          ...router.currentRoute.params,
          ...router.currentRoute.query,
        });

        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('%(count)% clinical training event was restored successfully', { count: ids.length }),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(ClinicalTrainingEventsMutations.SET_ALL_STATUS, ListStatus.Failed);

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
};
